import { useWindowSize } from 'hooks';
import React, { useEffect } from 'react';
import styled from 'styled-components';
import { detectRTL, getNumberOfPages } from '../helpers';
import { media, SMALL } from '../helpers/media';
import PaginationIconPrevious from './icons/Left';
import PaginationIconNext from './icons/Right';

const defaultComponentOptions = {
  rangePrefixText: 'Showing',
  rangeSeparatorText: 'of',
  rangeSuffixText: 'entries',
};

const PaginationWrapper = styled.nav`
  display: flex;
  flex: 1 1 auto;
  justify-content: flex-start;
  align-items: center;
  box-sizing: border-box;
  padding: 16px 4px 20px;
  width: 100%;
  font-weight: normal;
  font-size: 13px;
  letter-spacing: 0.04em;
  color: #888;
  min-height: ${({ minHeight = '56px' }) => minHeight};
  background-color: transparent;
  border: none;
`;

const Button = styled.button`
  position: relative;
  display: block;
  user-select: none;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 5px;
  padding: 0;
  margin: 0 4px;
  cursor: pointer;
  transition: 400ms;
  color: ${({ theme }) => theme.colors.primary};
  fill: ${({ theme }) => theme.colors.primary};
  background-color: #f5f9ff;
  font-weight: normal;
  font-size: 15px;
  letter-spacing: 0.04em;
  &:disabled:not(.active) {
    cursor: unset;
    color: #888;
    fill: #888;
    background-color: #dedede;
  }
  &:hover:not(:disabled),
  /* &:focus, */
  &.active {
    outline: none;
    color: #fff;
    fill: #fff;
    background-color: ${({ theme }) => theme.colors.primary};
  }
  &.active {
    cursor: unset;
  }
  > svg {
    width: 22px;
  }
  ${(props) => props.isRTL && 'transform: scale(-1, -1)'};
`;

const PageList = styled.div`
  display: flex;
  align-items: center;
  border-radius: 4px;
  white-space: nowrap;
  ${media.sm`
    width: 100%;
    justify-content: space-around;
  `};
`;

const Range = styled.span`
  flex-shrink: 1;
  user-select: none;
  margin: 0 24px;
  margin-left: 0;
`;

const Pagination = (props) => {
  const {
    rowsPerPage,
    rowCount,
    onPageChange,
    currentPage,
    componentOptions,
    minHeight,
    onPropsChange,
  } = props;
  const windowSize = useWindowSize();
  const shouldShow = windowSize.width > SMALL;
  const isRTL = detectRTL();
  const numPages = getNumberOfPages(rowCount, rowsPerPage);
  const lastIndex = currentPage * rowsPerPage;
  const firstIndex = lastIndex - rowsPerPage + 1;
  const disabledLesser = currentPage === 1;
  const disabledGreater = currentPage === numPages;
  const options = { ...defaultComponentOptions, ...componentOptions };
  const range =
    currentPage === numPages
      ? `${options.rangePrefixText} ${firstIndex}-${rowCount} ${options.rangeSeparatorText} ${rowCount} ${options.rangeSuffixText}`
      : `${options.rangePrefixText} ${firstIndex}-${lastIndex} ${options.rangeSeparatorText} ${rowCount} ${options.rangeSuffixText}`;

  const handlePrevious = React.useCallback(
    () => onPageChange(currentPage - 1),
    [currentPage, onPageChange]
  );

  const handleNext = React.useCallback(
    () => onPageChange(currentPage + 1),
    [currentPage, onPageChange]
  );

  const handlePageChange = React.useCallback((page) => onPageChange(page), [onPageChange]);

  useEffect(() => {
    onPropsChange && onPropsChange(props);
  }, []);

  return (
    <PaginationWrapper className="rdt_Pagination" minHeight={minHeight}>
      {shouldShow && <Range>{range}</Range>}
      <PageList>
        <Button
          id="pagination-previous-page"
          type="button"
          aria-label="Previous Page"
          aria-disabled={disabledLesser}
          onClick={handlePrevious}
          disabled={disabledLesser}
          isRTL={isRTL}
        >
          <PaginationIconPrevious />
        </Button>

        {numPages <= 6 ? (
          [...Array(numPages).keys()]
            .map((k) => ++k)
            .map((page) => (
              <PageButton
                key={`page-${page}`}
                page={page}
                currentPage={currentPage}
                isRTL={isRTL}
                onClick={handlePageChange}
              />
            ))
        ) : (
          <>
            {/* Always print first page button */}
            <PageButton
              page={1}
              currentPage={currentPage}
              isRTL={isRTL}
              onClick={handlePageChange}
            />

            {/* Print "..." only if currentPage is > 3 */}
            {currentPage > 3 && (
              <PageButton
                page={2}
                dots
                currentPage={currentPage}
                isRTL={isRTL}
                onClick={handlePageChange}
              />
            )}

            {/* special case where last page is selected... */}
            {currentPage === numPages && (
              <PageButton
                page={currentPage - 2}
                currentPage={currentPage}
                isRTL={isRTL}
                onClick={handlePageChange}
              />
            )}

            {/* Print previous number button if currentPage > 2 */}
            {currentPage > 2 && (
              <PageButton
                page={currentPage - 1}
                currentPage={currentPage}
                isRTL={isRTL}
                onClick={handlePageChange}
              />
            )}

            {/* Print current page number button as long as it not the first or last page */}
            {currentPage !== 1 && currentPage !== numPages && (
              <PageButton
                page={currentPage}
                currentPage={currentPage}
                isRTL={isRTL}
                onClick={handlePageChange}
              />
            )}

            {/* print next number button if currentPage < lastPage - 1 */}
            {currentPage < numPages - 1 && (
              <PageButton
                page={currentPage + 1}
                currentPage={currentPage}
                isRTL={isRTL}
                onClick={handlePageChange}
              />
            )}

            {/* special case where first page is selected... */}
            {currentPage === 1 && (
              <PageButton
                page={currentPage + 2}
                currentPage={currentPage}
                isRTL={isRTL}
                onClick={handlePageChange}
              />
            )}

            {/* print "..." if currentPage is < lastPage - 2 */}
            {currentPage < numPages - 2 && (
              <PageButton
                page={numPages - 1}
                dots
                currentPage={currentPage}
                isRTL={isRTL}
                onClick={handlePageChange}
              />
            )}

            {/* Always print last page button if there is more than 1 page */}
            <PageButton
              page={numPages}
              currentPage={currentPage}
              isRTL={isRTL}
              onClick={handlePageChange}
            />
          </>
        )}

        <Button
          id="pagination-next-page"
          type="button"
          aria-label="Next Page"
          aria-disabled={disabledGreater}
          onClick={handleNext}
          disabled={disabledGreater}
          isRTL={isRTL}
        >
          <PaginationIconNext />
        </Button>
      </PageList>
    </PaginationWrapper>
  );
};

const PageButton = ({ page, currentPage, isRTL, dots, onClick }) => {
  return (
    <Button
      id={`pagination-page-${page}`}
      type="button"
      aria-label={`Page ${page}`}
      aria-disabled={page === currentPage}
      onClick={() => onClick(page)}
      disabled={page === currentPage}
      isRTL={isRTL}
      className={page === currentPage ? 'active' : ''}
    >
      {dots ? '...' : page}
    </Button>
  );
};

export default Pagination;
